// =============================================================================
// io/parsers/tt.ts — Trading Technologies fills export (XLSX).
// -----------------------------------------------------------------------------
// PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3):
// `TT_MONTHS`, `TT_MONTH_NUM`, `ttContractToSymbol`, `ttDateToISO`,
// `isTtSyntheticFill`, `adaptTtFillRow`, and PATH 3 of the workbook dispatcher.
//
// ── 🚨 TT REPORTS ONE FILL TWO OR THREE TIMES, AND ONLY ONE ROW IS REAL ──
//
// TT's Synthetic Engine (Algo SE, Autospreader SE, ADL SE…) works an order on
// TT's own servers and sends a CHILD order to the exchange to actually get
// filled. The fills export then lists the same fill on every leg of the chain.
// From Josh's real export of 2026-07-27:
//
//   time          exch   B/S qty  price      route     order     parent
//   12:23:54.785  CME    S   6    28696.00   TT SIM    a6d96ad8  1e7a8d62  ← REAL
//   12:23:54.785  CME    S   4    28695.75   TT SIM    a6d96ad8  1e7a8d62  ← REAL
//   12:23:54.792  CME*   S   6    28696.00   Algo SE   1e7a8d62  917f8b0b  ← echo
//   12:23:54.794  CME*   S   6    28696.00   Algo SE   917f8b0b  —         ← echo
//   12:23:54.805  CME*   S   4    28695.75   Algo SE   1e7a8d62  917f8b0b  ← echo
//   12:23:54.807  CME*   S   4    28695.75   Algo SE   917f8b0b  —         ← echo
//
// Ten contracts sold; thirty imported. **On that file the whole day nets −20
// contracts of MNQ when the trader finished FLAT** — a phantom open position, a
// wrong average price on every episode after it, and P&L reconciling with
// nothing.
//
// ── THE ABSOLUTE ORACLE, AND IT IS WHY THIS IS NOT A JUDGEMENT CALL ──
//
// A day that ends flat must net to exactly zero. On the real export:
//
//   import everything                 MNQ −20   ✗
//   drop rows whose Route ends "SE"   MNQ   0   ✓
//   drop rows whose Exchange ends "*" MNQ   0   ✓
//
// Both markers agree on all four rows. `Route` is the trigger because it names
// the destination and the SE family is matched as a family — every synthetic
// engine has this shape, so pinning the literal "Algo SE" just means the next
// trader files the same bug with a different label. The exchange asterisk is
// left as CORROBORATION, so a missing Exchange column cannot silently switch the
// filter off.
//
// ⚠ DELIBERATELY NOT A DE-DUPLICATE-BY-PRICE-AND-QUANTITY RULE. Two genuine
// fills of the same size at the same price in the same second are ordinary
// trading; dropping one loses a real fill. Only the routing tells them apart.
//
// ── ⚠ AND THE TRAP THE OTHER WAY, WHICH THE REAL FILE EXPOSED ──
//
// TT ALSO writes several rows per order that are all genuine. Its `P/F` column
// marks a partial versus the completing fill, and one order arrives as:
//
//   12:41:25.865  S 10 @ 28676.00  F
//   12:41:25.865  S  7 @ 28676.25  P
//   12:41:25.865  S  1 @ 28676.50  P     ← all one `TTOrderID`, all real, 18 lots
//
// Same order id, same timestamp, three different prices. **De-duplicating by
// `TTOrderID` — the obvious defence against the synthetic rows — would throw
// away 8 of those 18 contracts.** The two problems look identical and have
// opposite fixes; that is why the filter is on routing and nothing else.
//
// 13 of the 57 orders in the real file have more than one row. The oracle covers
// this too: dropping only the SE rows nets exactly zero, which it could not do
// if any of the multi-row orders were duplicates.
// =============================================================================

import { getCol, numCol, type Row } from './columns';
import { parseAtasFillsFromRows, type AtasFillsResult } from './atas';
import type { ContractSpec } from '../../domain/trade';

/** Month name → CME month code. */
const TT_MONTHS: Record<string, string> = {
  JAN: 'F', FEB: 'G', MAR: 'H', APR: 'J', MAY: 'K', JUN: 'M',
  JUL: 'N', AUG: 'Q', SEP: 'U', OCT: 'V', NOV: 'X', DEC: 'Z',
};

/** Month name → calendar month number. */
const TT_MONTH_NUM: Record<string, string> = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
};

/**
 * TT contract `"NQ Jun26"` → `"NQM26"`.
 *
 * `findContract` reduces that to the root (NQ) for the account/contract link,
 * and `fullContractSym` keeps the dated symbol for the chart. Falls back to the
 * de-spaced upper-case string when the shape is unexpected — `findContract`
 * still tries, and the save guard holds it if nothing resolves.
 */
export function ttContractToSymbol(raw: string | null | undefined): string {
  const s = String(raw || '').trim();
  const m = s.match(/^([A-Za-z0-9]+)\s+([A-Za-z]{3})\s*(\d{2,4})$/);
  if (!m) return s.toUpperCase().replace(/\s+/g, '');
  const code = TT_MONTHS[(m[2] as string).toUpperCase()];
  return code
    ? `${(m[1] as string).toUpperCase()}${code}${(m[3] as string).slice(-2)}`
    : s.toUpperCase().replace(/\s+/g, '');
}

/**
 * TT date `"08May26"` → `"2026-05-08"`.
 *
 * ⚠ Returns `''` rather than today's date when it does not match, so the adapter
 * DROPS the fill instead of dating it to whenever the import happened. A fill on
 * the wrong day is worse than a fill that is visibly missing.
 */
export function ttDateToISO(raw: string | null | undefined): string {
  const m = String(raw || '').trim().match(/^(\d{1,2})([A-Za-z]{3})(\d{2,4})$/);
  if (!m) return '';
  const mon = TT_MONTH_NUM[(m[2] as string).toUpperCase()];
  if (!mon) return '';
  const y = m[3] as string;
  const yr = y.length === 2 ? `20${y}` : y;
  return `${yr}-${mon}-${String(+(m[1] as string)).padStart(2, '0')}`;
}

/**
 * A synthetic-engine echo, not a fill that happened.
 *
 * ⚠ `(^|\s)SE$` — the whole SE FAMILY, anchored at the end of the route. See the
 * header: matching "Algo SE" literally would miss Autospreader SE, ADL SE, and
 * whatever TT ships next.
 */
const TT_SYNTHETIC_ROUTE = /(^|\s)SE$/i;

export function isTtSyntheticFill(r: Row): boolean {
  return TT_SYNTHETIC_ROUTE.test(String(getCol(r, 'Route') || '').trim());
}

/**
 * One TT fill row → the shape the position-episode walker reads, or `null`.
 *
 * ⚠ TT's `Date` and `Time` are TEXT, not Excel serials, so this path never
 * touches `excelSerialToParts`. `Time` also arrives with a trailing space in the
 * real export (`"15:03:56.084 "`, written with `xml:space="preserve"`), which is
 * why it is trimmed rather than concatenated raw.
 *
 * ⚠ TIME IS OFTEN ABSENT from a TT export. When it is, the row emits date only:
 * the walker's sort is stable, so fills pair in FILE ORDER, and the trade's
 * timeIn/timeOut come out blank — no chart and no excursion until TT includes
 * the time. Degraded, deliberately, and visibly.
 */
export function adaptTtFillRow(r: Row): Row | null {
  const sym = ttContractToSymbol(getCol(r, 'Contract', 'Instrument', 'Symbol'));
  const px = numCol(r, 'Price', 'FillPrice', 'Fill Price');
  const qty = getCol(r, 'FillQty', 'Fill Qty', 'Qty', 'Quantity', 'Volume');
  const bs = String(getCol(r, 'B/S', 'BuySell', 'Side', 'Direction') || '').trim();
  const dateISO = ttDateToISO(getCol(r, 'Date'));
  if (!sym || !px || !qty || !bs || !dateISO) return null;

  const timeStr = String(getCol(r, 'Time') || '').trim();
  return {
    Account: getCol(r, 'Account', 'TradeAccount') || '',
    Instrument: sym,
    // 'B' / 'S' → the walker's `/^b/` test.
    Direction: bs,
    Volume: qty,
    Price: String(px),
    Time: timeStr ? `${dateISO} ${timeStr}` : dateISO,
    // ⚠ The ORDER id, and several genuine fills share one. It is carried for
    // traceability only — nothing de-duplicates on it, and the header explains
    // why doing so would delete real contracts.
    Id: getCol(r, 'TTOrderID', 'TT Order ID', 'OrderID', 'Order ID', 'Id') || '',
  };
}

export interface TtResult extends AtasFillsResult {
  /** Synthetic-engine rows ignored. Reported, never silent. */
  synthetic: number;
  /**
   * Set when EVERY row was synthetic — see `parseTtFills`. The caller shows this
   * instead of a trade list.
   */
  onlySynthetic: boolean;
}

/**
 * TT fills → round trips, through the shared position-episode walker.
 *
 * ⚠ The synthetic filter runs BEFORE the adapter rather than returning `null`
 * from inside it, so "dropped as a duplicate" stays a separate number from
 * "dropped as malformed". Those are different problems and the user is owed the
 * difference.
 */
export function parseTtFills(
  rows: readonly Row[],
  catalogue: readonly ContractSpec[],
  today: string,
): TtResult {
  const exchangeFills = rows.filter((r) => !isTtSyntheticFill(r));
  const synthetic = rows.length - exchangeFills.length;
  const adapted = exchangeFills
    .map(adaptTtFillRow)
    .filter((x): x is Row => x !== null);

  // ⚠ Every row synthetic contradicts the premise — a synthetic order can only
  // fill by way of a child order at the exchange — so it should not happen. But
  // falling through to the generic "no recognised sheet" error would blame the
  // file's shape for something this filter did. Say what actually happened.
  if (!adapted.length && synthetic) {
    return { trades: [], openPositions: [], skipped: 0, synthetic, onlySynthetic: true };
  }

  const r = parseAtasFillsFromRows(adapted, catalogue, today);
  return { ...r, synthetic, onlySynthetic: false };
}
